import { MutationTree, GetterTree, ActionTree } from "vuex";
import { RootState } from "vuefront-store";
import {
  ACCOUNT_WISHLIST,
  ACCOUNT_WISHLIST_ADD,
  ACCOUNT_WISHLIST_REMOVE,
  ACCOUNT_WISHLIST_CLEAR,
  ACCOUNT_WISHLIST_MERGE_GUEST,
  LEGACY_WISHLIST,
  LEGACY_WISHLIST_ADD,
  LEGACY_WISHLIST_REMOVE,
} from "~/core/account/graphql";
import {
  clearGuestWishlistIds,
  hasGuestWishlistSelection,
  readGuestWishlistIds,
  readGuestWishlistItems,
  writeGuestWishlistIds,
  writeGuestWishlistItems,
} from "~/core/account/guestWishlist";
import {
  wishlistMergeNotice,
  wishlistServiceError,
} from "~/core/account/wishlistCopy";
import { productCardPath } from "~/core/utils/canonicalPath";

export type WishlistItem = {
  id: string;
  productId: string;
  name: string;
  model?: string;
  href?: string | null;
  url?: string | null;
  image?: string | null;
  imageLazy?: string | null;
  images?: Array<{ image?: string; imageLazy?: string; imageBig?: string }>;
  price: string;
  priceRaw?: number | null;
  previousPriceFormatted?: string | null;
  priceChanged?: boolean;
  inStock: boolean;
  stockStatus: string;
  available: boolean;
  minimum: number;
  requiresOptions: boolean;
  cartEligible: boolean;
  lowStock?: boolean;
  finalSale?: boolean;
};

export type State = {
  wishlist: WishlistItem[];
  count: number;
  loading: boolean;
  error: string;
  pending: string[];
  loadRequest: number;
};

export const state = (): State => ({
  wishlist: [],
  count: 0,
  loading: false,
  error: "",
  pending: [],
  loadRequest: 0,
});
export const getters: GetterTree<State, RootState> = {
  get: (state) => state.wishlist,
  count: (state) => state.count,
  loading: (state) => state.loading,
  error: (state) => state.error,
  pending: (state) => (id: string | number) =>
    state.pending.includes(String(id)),
};

function normalizeLegacy(item: any): WishlistItem {
  const id = String(item.id);
  const inStock = item.stock !== false;
  const requiresOptions =
    Array.isArray(item.options) && item.options.length > 0;
  return {
    ...item,
    id,
    productId: id,
    href: item.url || null,
    price: String(item.price || ""),
    inStock,
    stockStatus: inStock ? "In stock" : "Out of stock",
    available: true,
    minimum: Math.max(1, Number(item.minimum || 1)),
    requiresOptions,
    cartEligible: inStock && !requiresOptions,
  };
}

function normalizeAccount(item: any): WishlistItem {
  const id = String(item.productId);
  return {
    ...item,
    id,
    productId: id,
    url: item.href,
    imageLazy: item.image,
    price: item.priceFormatted ?? item.price ?? "",
  };
}

function guestItemFromProduct(product: any): WishlistItem {
  const id = String(product?.productId ?? product?.id ?? "");
  const inStock = product?.stock !== false;
  const requiresOptions =
    Array.isArray(product?.options) && product.options.length > 0;
  const finalSale = Array.isArray(product?.categories)
    ? product.categories.some((category: any) => String(category?.id) === "62")
    : false;
  return {
    id,
    productId: id,
    name: String(product?.name || product?.model || "Saved product"),
    model: product?.model,
    href: productCardPath(product),
    url: productCardPath(product),
    image: product?.image || product?.imageLazy || null,
    imageLazy: product?.imageLazy || product?.image || null,
    images: Array.isArray(product?.images) ? product.images.slice(0, 3) : [],
    price: String(product?.special || product?.price || ""),
    inStock,
    stockStatus: inStock ? "In stock" : "Out of stock",
    available: true,
    minimum: Math.max(1, Number(product?.minimum || 1)),
    requiresOptions,
    cartEligible: inStock && !requiresOptions,
    lowStock: product?.low_stock === true,
    finalSale,
  };
}

function priceNumber(value: unknown): number | null {
  const parsed = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeGuestProduct(product: any): WishlistItem {
  const normalized = guestItemFromProduct(product);
  const regularPrice = priceNumber(product.price);
  const specialPrice = priceNumber(product.special);
  if (
    product.special &&
    regularPrice !== null &&
    specialPrice !== null &&
    specialPrice < regularPrice
  ) {
    return {
      ...normalized,
      price: String(product.special),
      priceRaw: specialPrice,
      previousPriceRaw: regularPrice,
      previousPriceFormatted: String(product.price),
      priceChanged: true,
      special: String(product.special),
    };
  }
  return normalized;
}

function persistGuestWishlist(items: WishlistItem[]): void {
  writeGuestWishlistIds(items.map((item) => item.id));
  writeGuestWishlistItems(items);
}

export const mutations: MutationTree<State> = {
  setWishlist(state, payload: any) {
    const items = Array.isArray(payload) ? payload : payload?.items || [];
    state.wishlist = items.map((item: any) =>
      item.productId !== undefined && item.priceFormatted !== undefined
        ? normalizeAccount(item)
        : normalizeLegacy(item)
    );
    state.count = Number(payload?.count ?? state.wishlist.length);
    state.error = "";
  },
  setLoading(state, value: boolean) {
    state.loading = value;
  },
  beginLoad(state) {
    state.loadRequest += 1;
    state.loading = true;
  },
  finishLoad(state, requestId: number) {
    if (requestId === state.loadRequest) state.loading = false;
  },
  invalidateLoad(state) {
    state.loadRequest += 1;
    state.loading = false;
  },
  applyWishlist(
    state,
    { payload, requestId }: { payload: any; requestId: number }
  ) {
    if (requestId !== state.loadRequest) return;
    const items = Array.isArray(payload) ? payload : payload?.items || [];
    state.wishlist = items.map((item: any) =>
      item.productId !== undefined && item.priceFormatted !== undefined
        ? normalizeAccount(item)
        : normalizeLegacy(item)
    );
    state.count = Number(payload?.count ?? state.wishlist.length);
    state.error = "";
  },
  setError(state, value: string) {
    state.error = value;
  },
  setPending(state, { id, value }: { id: string; value: boolean }) {
    const normalized = String(id);
    state.pending = value
      ? Array.from(new Set([...state.pending, normalized]))
      : state.pending.filter((item) => item !== normalized);
  },
  clearPrivate(state) {
    state.wishlist = [];
    state.count = 0;
    state.loading = false;
    state.error = "";
    state.pending = [];
    state.loadRequest += 1;
  },
};

const serviceError = wishlistServiceError;

export const actions: ActionTree<State, RootState> = {
  async load({ state, commit, rootGetters, dispatch }) {
    if (typeof window === "undefined") return { ok: true, items: [] };
    commit("beginLoad");
    const requestId = state.loadRequest;
    const authenticated = Boolean(rootGetters["common/customer/auth"]);
    commit("setError", "");
    try {
      if (authenticated) {
        const { data } = await this.$vfapollo.query({
          query: ACCOUNT_WISHLIST,
          fetchPolicy: "network-only",
        });
        if (
          requestId !== state.loadRequest ||
          !rootGetters["common/customer/auth"]
        )
          return { ok: false, stale: true };
        commit("applyWishlist", { payload: data.accountWishlistV2, requestId });
      } else {
        const hasLocalSelection = hasGuestWishlistSelection();
        const localIds = readGuestWishlistIds();
        const cachedItems = readGuestWishlistItems().map(normalizeLegacy);
        const { data } = await this.$vfapollo.query({
          query: LEGACY_WISHLIST,
          fetchPolicy: "network-only",
        });
        let items = data.wishlist || [];
        const serverItems = new Map(
          items.map((item: any) => [String(item.id), item])
        );
        const cachedById = new Map(
          cachedItems.map((item: WishlistItem) => [item.id, item])
        );
        const desiredIds = hasLocalSelection
          ? localIds
          : items.map((item: any) => String(item.id));
        const replayFailures: string[] = [];
        for (const id of desiredIds) {
          if (!serverItems.has(id)) {
            const result = await dispatch("guestMutation", {
              operation: "add",
              productId: id,
              persist: false,
              commitResult: false,
              reportError: false,
            });
            if (result.ok) {
              items = result.items;
              for (const item of items) serverItems.set(String(item.id), item);
            } else replayFailures.push(id);
          }
        }
        if (
          requestId !== state.loadRequest ||
          rootGetters["common/customer/auth"]
        )
          return { ok: false, stale: true };
        const resolvedItems = desiredIds
          .map((id: string) => serverItems.get(id) || cachedById.get(id))
          .filter(Boolean);
        commit("applyWishlist", { payload: resolvedItems, requestId });
        writeGuestWishlistIds(desiredIds);
        writeGuestWishlistItems(
          resolvedItems.map((item: any) =>
            item.productId !== undefined && item.priceFormatted !== undefined
              ? normalizeAccount(item)
              : normalizeLegacy(item)
          )
        );
        if (replayFailures.length)
          return { ok: true, localOnly: true, failedIds: replayFailures };
      }
      return { ok: true };
    } catch {
      if (!authenticated && hasGuestWishlistSelection()) {
        const localIds = new Set(readGuestWishlistIds());
        const cachedItems = readGuestWishlistItems()
          .map(normalizeLegacy)
          .filter((item) => localIds.has(item.id));
        commit("applyWishlist", { payload: cachedItems, requestId });
        return { ok: true, localOnly: true };
      }
      commit("setError", serviceError);
      return { ok: false, error: serviceError };
    } finally {
      commit("finishLoad", requestId);
    }
  },

  async afterAuthentication({ commit, dispatch }) {
    if (typeof window === "undefined") return { ok: true };
    commit("invalidateLoad");
    let serverMergeOk = false;
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_WISHLIST_MERGE_GUEST,
      });
      serverMergeOk = Boolean(data.accountWishlistMergeGuestV2?.ok);
    } catch {
      serverMergeOk = false;
    }
    let browserMergeOk = true;
    for (const productId of readGuestWishlistIds()) {
      try {
        const { data } = await this.$vfapollo.mutate({
          mutation: ACCOUNT_WISHLIST_ADD,
          variables: { productId },
        });
        if (!data.accountWishlistAddV2?.ok) browserMergeOk = false;
      } catch {
        browserMergeOk = false;
      }
    }
    const result = await dispatch("load");
    if (result.ok && serverMergeOk && browserMergeOk) clearGuestWishlistIds();
    else commit("setError", wishlistMergeNotice);
    return { ...result, serverMergeOk, browserMergeOk };
  },

  async addExplicit({ state, commit, rootGetters, dispatch }, { product }) {
    const id = String(product.productId ?? product.id);
    if (state.wishlist.some((item) => item.id === id))
      return { ok: true, unchanged: true };
    commit("setPending", { id, value: true });
    try {
      if (!rootGetters["common/customer/auth"]) {
        const localItems = [
          ...state.wishlist,
          normalizeGuestProduct({ ...product, id, productId: id }),
        ];
        commit("setWishlist", localItems);
        persistGuestWishlist(localItems);
        const result = await dispatch("guestMutation", {
          operation: "add",
          productId: id,
          persist: false,
          commitResult: false,
          reportError: false,
        });
        if (result.ok) {
          commit("setWishlist", result.items);
          persistGuestWishlist(result.items.map(normalizeLegacy));
          return result;
        }
        commit("setError", "");
        return { ok: true, localOnly: true };
      }
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_WISHLIST_ADD,
        variables: { productId: id },
      });
      const result = data.accountWishlistAddV2;
      if (result.wishlist) commit("setWishlist", result.wishlist);
      if (!result.ok) commit("setError", result.error?.message || serviceError);
      return result;
    } catch {
      commit("setError", serviceError);
      return {
        ok: false,
        error: { code: "SERVICE_UNAVAILABLE", message: serviceError },
      };
    } finally {
      commit("setPending", { id, value: false });
    }
  },

  async removeExplicit({ state, commit, rootGetters, dispatch }, payload) {
    const id = String(payload.productId ?? payload.id ?? payload.product?.id);
    const snapshot = [...state.wishlist];
    commit(
      "setWishlist",
      snapshot.filter((item) => item.id !== id)
    );
    commit("setPending", { id, value: true });
    try {
      if (!rootGetters["common/customer/auth"]) {
        persistGuestWishlist(state.wishlist);
        const result = await dispatch("guestMutation", {
          operation: "remove",
          productId: id,
          persist: false,
          commitResult: false,
          reportError: false,
        });
        if (result.ok) return result;
        commit("setError", "");
        return { ok: true, localOnly: true };
      }
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_WISHLIST_REMOVE,
        variables: { productId: id },
      });
      const result = data.accountWishlistRemoveV2;
      if (result.wishlist) commit("setWishlist", result.wishlist);
      if (!result.ok) {
        commit("setWishlist", snapshot);
        commit("setError", result.error?.message || serviceError);
      }
      return result;
    } catch {
      commit("setWishlist", snapshot);
      commit("setError", serviceError);
      return { ok: false, rolledBack: true };
    } finally {
      commit("setPending", { id, value: false });
    }
  },

  async clear({ state, commit, rootGetters, dispatch }) {
    if (!rootGetters["common/customer/auth"]) {
      const snapshot = [...state.wishlist];
      commit("setWishlist", []);
      writeGuestWishlistIds([]);
      writeGuestWishlistItems([]);
      let localOnly = false;
      for (const item of snapshot) {
        const result = await dispatch("guestMutation", {
          operation: "remove",
          productId: item.id,
          persist: false,
          commitResult: false,
          reportError: false,
        });
        if (!result?.ok) localOnly = true;
      }
      commit("setError", "");
      return localOnly ? { ok: true, localOnly: true } : { ok: true };
    }
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_WISHLIST_CLEAR,
      });
      const result = data.accountWishlistClearV2;
      if (result.wishlist) commit("setWishlist", result.wishlist);
      if (!result.ok) commit("setError", result.error?.message || serviceError);
      return result;
    } catch {
      commit("setError", serviceError);
      return { ok: false, error: { message: serviceError } };
    }
  },

  async guestMutation(
    { commit },
    {
      operation,
      productId,
      persist = true,
      commitResult = true,
      reportError = true,
    }
  ) {
    try {
      const mutation =
        operation === "add" ? LEGACY_WISHLIST_ADD : LEGACY_WISHLIST_REMOVE;
      const key = operation === "add" ? "addToWishlist" : "removeWishlist";
      const { data } = await this.$vfapollo.mutate({
        mutation,
        variables: {
          id: operation === "add" ? Number(productId) : String(productId),
        },
      });
      const items = data[key] || [];
      if (commitResult) commit("setWishlist", items);
      if (persist) persistGuestWishlist(items.map(normalizeLegacy));
      return { ok: true, items };
    } catch {
      if (reportError) commit("setError", serviceError);
      return { ok: false, error: { message: serviceError } };
    }
  },

  // Legacy product tiles retain toggle behavior during the compatibility window.
  async add({ state, dispatch }, { product }) {
    const products = Array.isArray(product) ? product : [product];
    const results = [];
    for (const item of products) {
      const exists = state.wishlist.some(
        (saved) => saved.id === String(item.id)
      );
      results.push(
        await dispatch(exists ? "removeExplicit" : "addExplicit", {
          product: item,
          productId: item.id,
        })
      );
    }
    return results;
  },
  async remove({ dispatch }, payload) {
    return dispatch("removeExplicit", payload);
  },
};
